import { combineEpics } from 'redux-observable';
import { filter, from, map, mergeMap, switchMap, withLatestFrom } from 'rxjs';

import module from './index';
import * as api from './wall_preview_rooms.api';

import { loadBlobAsImage } from 'utils/image_load';
import { catchAndLogError } from 'utils/common';

import type { Action } from '@reduxjs/toolkit';
import type { Epic } from 'redux-observable';
import type { RootState } from 'store/wos_store';
import type { EditUserRoomDraft, SaveUserRoomPUTData } from './wall_preview_rooms.types';

const { actions, selectors } = module;

const fetchUserRoomsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.fetchUserRooms.pending.match),
    mergeMap(() =>
      from(api.get()).pipe(
        map((rooms) => actions.fetchUserRooms.fulfilled(rooms)),
        catchAndLogError(actions.fetchUserRooms.rejected)
      )
    )
  );

/**
 * Fetches a wall preview background image as a blob, returning its size & blob as an object URL
 */
const fetchWallPreviewBackgroundEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.setWhichWallPreview.match),
    switchMap(({ payload }) =>
      from(loadBlobAsImage(payload.fullSrc)).pipe(
        map((img: HTMLImageElement) => ({
          naturalHeight: img.naturalHeight,
          naturalWidth: img.naturalWidth,
          blobSrc: img.src,
        })),
        map((payload) => actions.wallPreviewImageLoad.fulfilled(payload)),
        catchAndLogError(actions.unsetWhichWallPreview())
      )
    )
  );

const createUserRoomsEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.createUserRooms.pending.match),
    mergeMap(({ payload }) =>
      from(api.bulkCreate(payload)).pipe(
        map((rooms) => actions.createUserRooms.fulfilled(rooms)),
        catchAndLogError(actions.createUserRooms.rejected)
      )
    )
  );

const saveUserRoomsEpic: Epic<Action, Action, RootState> = (action$, state$) =>
  action$.pipe(
    filter(actions.saveUserRooms.pending.match),
    withLatestFrom(state$),
    mergeMap(([, state]) => {
      const editRoomDrafts = selectors.editRoomDrafts(state);
      const editedRoomData = Object.values(editRoomDrafts).map((room: EditUserRoomDraft) => {
        const { id, description, wallSize, shouldUpdateWidth } = room;
        const data: SaveUserRoomPUTData = { id, description };

        if (shouldUpdateWidth) {
          data.wall_size = wallSize;
        }

        return data;
      });

      return from(api.bulkUpdate(editedRoomData)).pipe(
        map((uploads) => actions.saveUserRooms.fulfilled(uploads)),
        catchAndLogError(actions.saveUserRooms.rejected)
      );
    })
  );

const deleteUserRoomEpic: Epic<Action, Action, RootState> = (action$) =>
  action$.pipe(
    filter(actions.deleteUserRoom.pending.match),
    mergeMap(({ payload }) =>
      from(api.destroy(payload)).pipe(
        map(() => actions.deleteUserRoom.fulfilled(payload)),
        catchAndLogError(actions.deleteUserRoom.rejected)
      )
    )
  );

const wallPreviewEpics = combineEpics(
  fetchUserRoomsEpic,
  fetchWallPreviewBackgroundEpic,
  createUserRoomsEpic,
  saveUserRoomsEpic,
  deleteUserRoomEpic
);
export default wallPreviewEpics;
